import { pbiBand, PBI_ADEQUATE_MIN } from "@/lib/pbi-bands"

import { alternateFormReason, shouldOfferAlternateForm } from "./leo-jobs"
import type { QuestionQualityHistory } from "./types"

export type PerformanceMetric = {
  label: string
  value: string
  tone?: "default" | "caution"
}

export type PerformanceInsight = {
  metrics: PerformanceMetric[]
  hint: string
  examineeCount: number | null
}

/** Matches the usage count in Leo spotted notes, e.g. "3 times". */
export const LEO_USAGE_PHRASE_RE = /(\d+ times)/i

export function noteContainsUsagePhrase(note: string): boolean {
  return LEO_USAGE_PHRASE_RE.test(note)
}

/** Item-analysis rows in the same order as the question preview sheet. */
export function performanceMetricsFor(
  history: QuestionQualityHistory,
): PerformanceMetric[] {
  const metrics: PerformanceMetric[] = []

  if (history.upper27Percent != null) {
    metrics.push({
      label: "Upper 27%",
      value: `${history.upper27Percent}%`,
    })
  }
  if (history.lower27Percent != null) {
    metrics.push({
      label: "Lower 27%",
      value: `${history.lower27Percent}%`,
    })
  }
  if (history.discriminationIndex != null) {
    metrics.push({
      label: "Disc. Index",
      value: history.discriminationIndex.toFixed(2),
    })
  }
  if (history.pointBiserial != null) {
    metrics.push({
      label: "PBI",
      value: history.pointBiserial.toFixed(2),
      tone: history.belowReviewFloor ? "caution" : "default",
    })
  }
  if (history.measuredDifficultyPercent != null) {
    metrics.push({
      label: "Difficulty %",
      value: `${history.measuredDifficultyPercent}%`,
    })
  }
  if (history.usedCount != null && history.usedCount > 0) {
    metrics.push({
      label: "Usage",
      value: `${history.usedCount}x`,
      tone: history.usedInSameCourse ? "caution" : "default",
    })
  }

  return metrics
}

/** Compact exam history for Leo spotted when there are no quality checks. */
export function performanceInsightFor(
  history: QuestionQualityHistory,
): PerformanceInsight | null {
  if (!history.sat) return null

  const metrics = performanceMetricsFor(history)
  if (metrics.length === 0) return null

  return {
    metrics,
    hint: performanceVariationHint(history),
    examineeCount: history.examineeCount ?? null,
  }
}

function performanceVariationHint(history: QuestionQualityHistory): string {
  if (history.belowReviewFloor) {
    return "PBI is below the review floor. Enhance or replace this item before drafting a variation."
  }
  if (shouldOfferAlternateForm(history)) {
    const usage = alternateFormReason(history).toLowerCase()
    if (history.usedInSameCourse) {
      return `This item was ${usage}. Draft another form so returning students see fresh wording while you keep the blueprint cell.`
    }
    return `This item was ${usage}. A new form changes surface features without leaving the blueprint cell.`
  }

  const band = pbiBand(history.pointBiserial)
  if ((history.pointBiserial ?? 0) >= PBI_ADEQUATE_MIN) {
    return `${band.label}. No quality flags on this item. Ask Leo for another wording or option set when you want variety without changing what it measures.`
  }
  return `${band.label}. Consider a variation that targets what strong and weak examinees miss.`
}
